import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { FileSpreadsheet, Upload, Download, Trash2, CheckCircle, AlertTriangle, Clock, Building2, FileText, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';

const SHEET_LABELS = [
  { key: 'emission_units',        label: 'Emission Units',       icon: '🏭' },
  { key: 'emission_limits',       label: 'Emission Limits',      icon: '📊' },
  { key: 'operational_limits',    label: 'Operational Limits',   icon: '⚙️' },
  { key: 'monitoring_requirements',label:'Monitoring',           icon: '📡' },
  { key: 'recordkeeping',         label: 'Recordkeeping',        icon: '📁' },
  { key: 'reporting_requirements', label: 'Reporting',           icon: '📋' },
  { key: 'testing_requirements',  label: 'Testing',              icon: '🧪' },
  { key: 'compliance_deadlines',  label: 'Compliance Deadlines', icon: '📅' },
  { key: 'control_devices',       label: 'Control Devices',      icon: '🔧' },
  { key: 'applicable_regulations',label: 'Regulations',          icon: '⚖️' },
];

const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid #E2E8F0', fontFamily:'var(--font-body)', fontSize:14, boxSizing:'border-box', background:'white' };
const lbl = { fontSize:12, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:4 };

export default function PermitDeconstruct() {
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [file, setFile] = useState(null);
  const [facilityId, setFacilityId] = useState('');
  const [facilities, setFacilities] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [extractedId, setExtractedId] = useState(null);
  const [saved, setSaved] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingJobId, setPendingJobId] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const [activeTab, setActiveTab] = useState('emission_limits');
  const fileInputRef = useRef();

  useEffect(() => { loadInit(); }, []);

  const loadInit = async () => {
    try {
      const [facRes, savedRes] = await Promise.all([
        api.get('/facilities'),
        api.get('/permit-deconstruct/list'),
      ]);
      setFacilities(facRes.data.facilities || []);
      setSaved(savedRes.data.deconstructions || []);
    } catch(e) { console.error(e); }
  };

  const handleFile = (f) => {
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['pdf','txt','docx'].includes(ext)) { toast.warn('Only PDF, DOCX, or TXT files supported'); return; }
    if (f.size > 50 * 1024 * 1024) { toast.warn('File too large. Max 50MB.'); return; }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const [extractProgress, setExtractProgress] = useState('');

  const extract = async () => {
    if (!file) return;
    try {
      setExtracting(true);
      setExtractProgress('Uploading permit...');
      const formData = new FormData();
      formData.append('permit', file);
      if (facilityId) formData.append('facility_id', facilityId);

      const { default: axios } = await import('axios');
      const token = localStorage.getItem('token');

      // Start job — returns immediately with jobId
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/permit-deconstruct/extract`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }, timeout: 30000 }
      );

      const { jobId } = res.data;
      setPendingJobId(jobId);
      setTimedOut(false);
      setExtractProgress('AI is reading the permit...');

      // Poll for completion
      const progressMessages = [
        'AI is reading the permit...',
        'Extracting emission units and limits...',
        'Extracting monitoring and reporting requirements...',
        'Extracting testing schedules and deadlines...',
        'Compiling regulations and control devices...',
        'Almost done...',
      ];
      let msgIdx = 0;
      const msgInterval = setInterval(() => {
        msgIdx = (msgIdx + 1) % progressMessages.length;
        setExtractProgress(progressMessages[msgIdx]);
      }, 8000);

      // Poll every 3 seconds for up to 5 minutes
      let attempts = 0;
      const maxAttempts = 240; // 12 minutes
      const result = await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          attempts++;
          if (attempts > maxAttempts) {
            clearInterval(poll);
            clearInterval(msgInterval);
            reject(new Error('Extraction timed out after 12 minutes. For very large scanned permits, try splitting into smaller sections.'));
            return;
          }
          try {
            const statusRes = await axios.get(
              `${process.env.REACT_APP_API_URL}/permit-deconstruct/status/${jobId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (statusRes.data.status === 'complete') {
              clearInterval(poll);
              clearInterval(msgInterval);
              resolve(statusRes.data);
            } else if (statusRes.data.status === 'error') {
              clearInterval(poll);
              clearInterval(msgInterval);
              reject(new Error(statusRes.data.error));
            }
          } catch(e) { /* keep polling */ }
        }, 3000);
      });

      setExtracted(result.data);
      setExtractedId(result.dbId);
      setStep('preview');
      loadInit();
      toast.success('Permit deconstructed successfully');
    } catch(e) {
      if (e.message && e.message.includes('timed out')) {
        setTimedOut(true);
        toast.warn('Still processing — click "Check for results" in a few minutes');
      } else {
        toast.error('Extraction failed: ' + (e.message || 'Unknown error'));
      }
    } finally {
      setExtracting(false);
      setExtractProgress('');
    }
  };

  const checkForResults = async () => {
    if (!pendingJobId) return;
    try {
      setExtracting(true);
      setTimedOut(false);
      setExtractProgress('Checking for results...');
      const { default: axios } = await import('axios');
      const token = localStorage.getItem('token');

      let attempts = 0;
      const maxAttempts = 240;
      const result = await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          attempts++;
          if (attempts > maxAttempts) {
            clearInterval(poll);
            reject(new Error('timed out'));
            return;
          }
          try {
            const statusRes = await axios.get(
              `${process.env.REACT_APP_API_URL}/permit-deconstruct/status/${pendingJobId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (statusRes.data.status === 'complete') {
              clearInterval(poll);
              resolve(statusRes.data);
            } else if (statusRes.data.status === 'error') {
              clearInterval(poll);
              reject(new Error(statusRes.data.error));
            }
          } catch(e) {}
        }, 3000);
      });

      setExtracted(result.data);
      setExtractedId(result.dbId);
      setPendingJobId(null);
      setStep('preview');
      loadInit();
      toast.success('Permit deconstructed successfully');
    } catch(e) {
      if (e.message?.includes('timed out')) {
        setTimedOut(true);
        toast.warn('Still processing — try again in a few minutes');
      } else {
        toast.error('Failed: ' + e.message);
      }
    } finally {
      setExtracting(false);
      setExtractProgress('');
    }
  };

  const downloadExcel = async (data, id) => {
    try {
      setDownloading(true);
      const { default: axios } = await import('axios');
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/permit-deconstruct/excel`,
        { data: data || extracted, id: id || extractedId },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 120000,
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(data || extracted)?.facility?.name?.replace(/[^a-zA-Z0-9]/g,'_') || 'Permit'}_Deconstruct.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Excel downloaded');
    } catch(e) {
      toast.error('Download failed: ' + (e.response?.data?.error || e.message));
    } finally { setDownloading(false); }
  };

  const deleteSaved = async (id) => {
    if (!window.confirm('Delete this deconstruction?')) return;
    await api.delete(`/permit-deconstruct/${id}`);
    loadInit();
    toast.success('Deleted');
  };

  const previewData = extracted;

  const countItems = (key) => previewData?.[key]?.length || 0;

  const getDeadlineStatus = (dl) => {
    if (!dl.due_date) return 'unknown';
    try {
      const due = new Date(dl.due_date);
      const days = Math.floor((due - new Date()) / (1000*60*60*24));
      if (days < 0) return 'overdue';
      if (days < 30) return 'soon';
      return 'ok';
    } catch { return 'unknown'; }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:3 }}>
            <FileSpreadsheet size={20} color="#0066FF" />
            <h1 style={{ fontFamily:'var(--font-heading)', fontSize:20, fontWeight:800, color:'#0A1628', margin:0 }}>Permit Deconstruct</h1>
          </div>
          <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)' }}>
            Upload any air permit — AI extracts all conditions, limits, monitoring, testing, and deadlines into a 11-sheet Excel workbook
          </div>
        </div>
        {step !== 'upload' && (
          <button onClick={() => { setStep('upload'); setFile(null); setExtracted(null); setExtractedId(null); }}
            style={{ padding:'7px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13 }}>
            New Deconstruction
          </button>
        )}
      </div>

      {/* ── UPLOAD STEP ── */}
      {step === 'upload' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:20 }}>
          <div>
            {/* Drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={e => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false); }}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{ border:`2px dashed ${isDragging?'#0066FF':'#CBD5E1'}`, borderRadius:12, padding:'48px 24px', textAlign:'center', cursor:'pointer', background: isDragging?'#EFF6FF':'#F8FAFC', transition:'all 0.2s', marginBottom:16 }}>
              <input ref={fileInputRef} type="file" accept=".pdf,.docx,.txt" style={{ display:'none' }} onChange={e => { if(e.target.files[0]) handleFile(e.target.files[0]); e.target.value=''; }} />
              <Upload size={40} color={isDragging?'#0066FF':'#CBD5E1'} style={{ margin:'0 auto 14px', display:'block' }} />
              {file ? (
                <>
                  <div style={{ fontFamily:'var(--font-heading)', fontSize:16, fontWeight:700, color:'#0066FF', marginBottom:4 }}>
                    {file.name}
                  </div>
                  <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)' }}>
                    {(file.size / 1024 / 1024).toFixed(1)} MB — Ready to deconstruct
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontFamily:'var(--font-heading)', fontSize:16, fontWeight:700, color:'#0A1628', marginBottom:4 }}>
                    Drop your air permit here
                  </div>
                  <div style={{ fontSize:13, color:'#6B7280', fontFamily:'var(--font-body)', marginBottom:8 }}>
                    or click to browse
                  </div>
                  <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)' }}>
                    PDF, Word (.docx), or TXT — up to 50MB
                  </div>
                </>
              )}
            </div>

            {/* Facility selector */}
            <div style={{ marginBottom:16 }}>
              <label style={lbl}>Link to Facility (optional)</label>
              <select value={facilityId} onChange={e => setFacilityId(e.target.value)} style={inp}>
                <option value="">No facility — standalone deconstruction</option>
                {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name||f.name}</option>)}
              </select>
            </div>

            <button onClick={extract} disabled={!file || extracting} className="btn btn-primary"
              style={{ width:'100%', justifyContent:'center', fontSize:15, padding:'12px', opacity: !file || extracting ? 0.5 : 1 }}>
              {extracting ? (
                <>
                  <div style={{ width:16, height:16, border:'2px solid white', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite', marginRight:8 }} />
                  {extractProgress || 'Starting extraction...'}
                </>
              ) : (
                <><FileSpreadsheet size={16} /> Deconstruct Permit</>
              )}
            </button>
            {timedOut && pendingJobId && (
              <div style={{ marginTop:12, padding:'12px 16px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8 }}>
                <div style={{ fontSize:13, color:'#92400E', fontFamily:'var(--font-body)', marginBottom:8, fontWeight:600 }}>
                  Extraction is still running in the background.
                </div>
                <div style={{ fontSize:12, color:'#92400E', fontFamily:'var(--font-body)', marginBottom:10 }}>
                  Large scanned permits can take 8-12 minutes. Click below to check if it's ready.
                </div>
                <button onClick={checkForResults} disabled={extracting} className="btn btn-primary"
                  style={{ fontSize:13, display:'flex', alignItems:'center', gap:6 }}>
                  {extracting ? 'Checking...' : '↻ Check for Results'}
                </button>
              </div>
            )}
            <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

            {/* What gets extracted */}
            <div className="card" style={{ padding:'18px 20px', marginTop:16 }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12 }}>What gets extracted — 11 sheets</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {[
                  { icon:'🏢', label:'Facility & Permit Summary' },
                  { icon:'🏭', label:'Emission Units (all EU parameters)' },
                  { icon:'📊', label:'Emission Limits (every pollutant)' },
                  { icon:'⚙️', label:'Operational Limits (hours, throughput)' },
                  { icon:'📡', label:'Monitoring Requirements (CEMS, parametric)' },
                  { icon:'📁', label:'Recordkeeping Requirements' },
                  { icon:'📋', label:'Reporting Requirements & Deadlines' },
                  { icon:'🧪', label:'Testing Requirements & Schedule' },
                  { icon:'📅', label:'Compliance Deadlines (color-coded)' },
                  { icon:'🔧', label:'Control Devices' },
                  { icon:'⚖️', label:'Applicable Regulations with eCFR URLs' },
                ].map((item, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, fontFamily:'var(--font-body)', color:'#374151' }}>
                    <span>{item.icon}</span> {item.label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Saved deconstructions sidebar */}
          <div>
            <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12 }}>Recent Deconstructions</div>
            {saved.length === 0 ? (
              <div style={{ padding:20, textAlign:'center', color:'#94a3b8', fontFamily:'var(--font-body)', fontSize:13, background:'#F8FAFC', borderRadius:10, border:'1px solid #E2E8F0' }}>
                No previous deconstructions
              </div>
            ) : saved.map(s => (
              <div key={s.id} className="card" style={{ padding:'12px 14px', marginBottom:8 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)', marginBottom:2 }}>
                  {s.permit_facility_name || s.facility_name || s.permit_filename}
                </div>
                <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)', marginBottom:8 }}>
                  {s.permit_type || 'Air Permit'} {s.permit_number ? `· ${s.permit_number}` : ''} · {new Date(s.created_at).toLocaleDateString()}
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <button onClick={() => downloadExcel(s.extracted_data, s.id)} disabled={downloading}
                    style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:5, padding:'5px 0', borderRadius:6, border:'1px solid #0066FF', background:'#EFF6FF', color:'#0066FF', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:11, fontWeight:600 }}>
                    <Download size={11} /> Excel
                  </button>
                  <button onClick={() => deleteSaved(s.id)}
                    style={{ padding:'5px 8px', borderRadius:6, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', color:'#DC2626' }}>
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── PREVIEW STEP ── */}
      {step === 'preview' && previewData && (
        <div>
          {/* Facility summary card */}
          <div className="card" style={{ padding:'16px 20px', marginBottom:16, borderLeft:'4px solid #0066FF' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
              <div>
                <div style={{ fontFamily:'var(--font-heading)', fontSize:16, fontWeight:800, color:'#0A1628', marginBottom:2 }}>
                  {previewData.facility?.name || 'Unnamed Facility'}
                </div>
                <div style={{ fontSize:13, color:'#6B7280', fontFamily:'var(--font-body)' }}>
                  {previewData.permit?.permit_type} · {previewData.permit?.permit_number} · {previewData.permit?.issuing_agency}
                </div>
                <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)', marginTop:2 }}>
                  Expires: {previewData.permit?.expiration_date || 'N/A'} · Renewal due: {previewData.permit?.renewal_due_date || 'N/A'}
                </div>
              </div>
              <button onClick={() => downloadExcel()} disabled={downloading} className="btn btn-primary"
                style={{ display:'flex', alignItems:'center', gap:6, fontSize:14 }}>
                <Download size={15} /> {downloading ? 'Generating...' : 'Download Excel (11 sheets)'}
              </button>
            </div>
          </div>

          {/* Summary stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10, marginBottom:16 }}>
            {[
              { label:'Emission Units', value: countItems('emission_units'), color:'#0066FF', bg:'#EFF6FF' },
              { label:'Emission Limits', value: countItems('emission_limits'), color:'#7C3AED', bg:'#F5F3FF' },
              { label:'Monitoring Req.', value: countItems('monitoring_requirements'), color:'#0891B2', bg:'#ECFEFF' },
              { label:'Reporting Req.', value: countItems('reporting_requirements'), color:'#D97706', bg:'#FFFBEB' },
              { label:'Testing Req.', value: countItems('testing_requirements'), color:'#16A34A', bg:'#F0FDF4' },
            ].map((s,i) => (
              <div key={i} style={{ padding:'12px 14px', background:s.bg, borderRadius:10, textAlign:'center' }}>
                <div style={{ fontFamily:'var(--font-heading)', fontSize:28, fontWeight:800, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:11, fontWeight:600, color:s.color, fontFamily:'var(--font-body)' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Deadline alerts */}
          {(() => {
            const overdue = (previewData.compliance_deadlines||[]).filter(d => getDeadlineStatus(d) === 'overdue');
            const soon = (previewData.compliance_deadlines||[]).filter(d => getDeadlineStatus(d) === 'soon');
            if (overdue.length === 0 && soon.length === 0) return null;
            return (
              <div style={{ marginBottom:16 }}>
                {overdue.length > 0 && (
                  <div style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, marginBottom:8, display:'flex', alignItems:'center', gap:8 }}>
                    <AlertTriangle size={14} color="#DC2626" />
                    <span style={{ fontSize:13, color:'#991B1B', fontFamily:'var(--font-body)', fontWeight:600 }}>
                      {overdue.length} overdue compliance deadline{overdue.length > 1 ? 's' : ''} — review Sheet 9 immediately
                    </span>
                  </div>
                )}
                {soon.length > 0 && (
                  <div style={{ padding:'10px 14px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, display:'flex', alignItems:'center', gap:8 }}>
                    <Clock size={14} color="#D97706" />
                    <span style={{ fontSize:13, color:'#92400E', fontFamily:'var(--font-body)', fontWeight:600 }}>
                      {soon.length} deadline{soon.length > 1 ? 's' : ''} due within 30 days
                    </span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Tab preview */}
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            {/* Tab bar */}
            <div style={{ display:'flex', overflowX:'auto', borderBottom:'1px solid #E2E8F0', background:'#F8FAFC' }}>
              {SHEET_LABELS.map(s => (
                <button key={s.key} onClick={() => setActiveTab(s.key)}
                  style={{ padding:'10px 14px', border:'none', borderBottom:`2px solid ${activeTab===s.key?'#0066FF':'transparent'}`, background: activeTab===s.key?'white':'transparent', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:12, fontWeight: activeTab===s.key?700:400, color: activeTab===s.key?'#0066FF':'#374151', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:5 }}>
                  {s.icon} {s.label}
                  <span style={{ fontSize:10, background: activeTab===s.key?'#EFF6FF':'#E2E8F0', color: activeTab===s.key?'#0066FF':'#6B7280', padding:'1px 6px', borderRadius:10, fontWeight:700 }}>
                    {countItems(s.key)}
                  </span>
                </button>
              ))}
            </div>

            {/* Table preview */}
            <div style={{ overflowX:'auto', maxHeight:400 }}>
              {activeTab === 'emission_limits' && (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'#F8FAFC', position:'sticky', top:0 }}>
                      {['EU ID','Condition','Pollutant','Limit Value','Unit','Avg Period','Limit Type','Regulation'].map(h => (
                        <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'#374151', borderBottom:'1px solid #E2E8F0', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(previewData.emission_limits||[]).map((lim,i) => (
                      <tr key={i} style={{ borderBottom:'1px solid #F1F5F9', background:i%2===0?'white':'#F8FAFC' }}>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', fontWeight:600, color:'#0066FF' }}>{lim.eu_id}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', color:'#374151' }}>{lim.condition_number}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', fontWeight:600 }}>{lim.pollutant}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', fontWeight:700, color:'#0A1628' }}>{lim.limit_value}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', color:'#6B7280' }}>{lim.limit_unit}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', color:'#374151' }}>{lim.averaging_period}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', fontSize:11 }}>
                          <span style={{ padding:'2px 8px', borderRadius:12, background: lim.limit_type?.includes('NSPS')||lim.limit_type?.includes('MACT')?'#F5F3FF':'#EFF6FF', color: lim.limit_type?.includes('NSPS')||lim.limit_type?.includes('MACT')?'#7C3AED':'#0066FF', fontWeight:600 }}>
                            {lim.limit_type}
                          </span>
                        </td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', color:'#6B7280', fontSize:11 }}>{lim.applicable_regulation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {activeTab === 'compliance_deadlines' && (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'#F8FAFC', position:'sticky', top:0 }}>
                      {['Status','EU ID','Action Required','Due Date','Frequency','Regulatory Driver'].map(h => (
                        <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'#374151', borderBottom:'1px solid #E2E8F0', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(previewData.compliance_deadlines||[]).map((dl,i) => {
                      const status = getDeadlineStatus(dl);
                      const statusConfig = {
                        overdue: { label:'OVERDUE', color:'#DC2626', bg:'#FEF2F2' },
                        soon:    { label:'Due Soon', color:'#D97706', bg:'#FFFBEB' },
                        ok:      { label:'Upcoming', color:'#16A34A', bg:'#F0FDF4' },
                        unknown: { label:'—', color:'#6B7280', bg:'#F8FAFC' },
                      }[status];
                      return (
                        <tr key={i} style={{ borderBottom:'1px solid #F1F5F9', background:i%2===0?'white':'#F8FAFC' }}>
                          <td style={{ padding:'7px 12px' }}>
                            <span style={{ padding:'2px 8px', borderRadius:12, background:statusConfig.bg, color:statusConfig.color, fontWeight:700, fontSize:10, fontFamily:'var(--font-body)' }}>{statusConfig.label}</span>
                          </td>
                          <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', fontWeight:600, color:'#0066FF' }}>{dl.eu_id}</td>
                          <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', maxWidth:280 }}>{dl.action_required}</td>
                          <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', fontWeight:600, whiteSpace:'nowrap' }}>{dl.due_date}</td>
                          <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', color:'#6B7280' }}>{dl.frequency}</td>
                          <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', color:'#6B7280', fontSize:11 }}>{dl.regulatory_driver}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {activeTab === 'emission_units' && (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'#F8FAFC', position:'sticky', top:0 }}>
                      {['EU ID','Description','Type','Capacity','Fuel','Stack ID','NSPS','MACT'].map(h => (
                        <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'#374151', borderBottom:'1px solid #E2E8F0', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(previewData.emission_units||[]).map((eu,i) => (
                      <tr key={i} style={{ borderBottom:'1px solid #F1F5F9', background:i%2===0?'white':'#F8FAFC' }}>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', fontWeight:700, color:'#0066FF' }}>{eu.eu_id}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)' }}>{eu.description}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', color:'#6B7280' }}>{eu.equipment_type}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', whiteSpace:'nowrap' }}>{eu.rated_capacity} {eu.capacity_unit}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', color:'#6B7280' }}>{eu.fuel_type}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)' }}>{eu.stack_id}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', fontSize:11, color:'#7C3AED' }}>{eu.nsps_subpart}</td>
                        <td style={{ padding:'7px 12px', fontFamily:'var(--font-body)', fontSize:11, color:'#0891B2' }}>{eu.neshap_mact_subpart}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Generic table for other tabs */}
              {!['emission_limits','compliance_deadlines','emission_units'].includes(activeTab) && (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'#F8FAFC', position:'sticky', top:0 }}>
                      {Object.keys((previewData[activeTab]||[{}])[0]||{}).map(k => (
                        <th key={k} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'#374151', borderBottom:'1px solid #E2E8F0', whiteSpace:'nowrap', fontFamily:'var(--font-body)', textTransform:'capitalize' }}>
                          {k.replace(/_/g,' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(previewData[activeTab]||[]).map((row,i) => (
                      <tr key={i} style={{ borderBottom:'1px solid #F1F5F9', background:i%2===0?'white':'#F8FAFC' }}>
                        {Object.values(row).map((val,j) => (
                          <td key={j} style={{ padding:'7px 12px', fontFamily:'var(--font-body)', color:'#374151', maxWidth:220 }}>{String(val)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div style={{ marginTop:16, display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={() => downloadExcel()} disabled={downloading} className="btn btn-primary"
              style={{ display:'flex', alignItems:'center', gap:6 }}>
              <Download size={15} /> {downloading ? 'Generating Excel...' : 'Download Full Excel (11 sheets)'}
            </button>
          </div>

          <div style={{ marginTop:10, padding:'10px 14px', background:'#F8FAFC', border:'1px solid #E2E8F0', borderRadius:8, fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', fontStyle:'italic' }}>
            AI-extracted permit data. Verify all limits, conditions, and deadlines against the original permit document before use in regulatory submittals.
          </div>
        </div>
      )}
    </div>
  );
}
